import { Box, MenuItem, Popover } from '@mui/material';
import { Button } from '@sweep-io/sweep-design';
import { useCallback, useMemo, useState } from 'react';
import { ObjectTypeLabelAndKeyTag } from '../common/ObjectTypeLabelAndKey';
import { highlightMatch } from '../../lib/highlightMatch';
import { SearchInput } from '../common/SearchInput';
import { filterItemsBySearch } from '../../lib/filterItemsBySearch';
import { getTypeNameFromVariant } from './helper';
import { AutomationFormVariant } from '../../types/enums/AutomationFormVariant';
import useCrmOrgPermissions from '../common/permissions/useCrmOrgPermissions';
import RestrictedTooltip from '../common/permissions/RestrictedTooltip';
import { PopoverProps } from '@mui/material/Popover/Popover';
import useObjectTypesWithFetch, {
  objectTypesPropertyFilter,
} from '../../hooks/useObjectTypesWithFetch';
import { ButtonVariants } from '@sweep-io/sweep-design/dist/components/Button/types';
import { useAutomationsContext } from './AutomationsContext';
import { DedupMatchingType } from '../../types/enums/DedupMatchingType';

const ITEM_HEIGHT = 40;

export interface AddAutomationButtonProps {
  onSelect: (object: string, type?: DedupMatchingType) => void;
  disabled: boolean;
  buttonText: string;
  crmOrgId: string;
  automationVariant: AutomationFormVariant;
  popoverProps: Pick<PopoverProps, 'anchorOrigin' | 'transformOrigin' | 'slotProps'>;
  startIconName: string;
  variant?: ButtonVariants;
}

export const AddAutomationButton = ({
  onSelect,
  disabled,
  buttonText,
  crmOrgId,
  automationVariant,
  popoverProps,
  startIconName,
  variant,
}: AddAutomationButtonProps) => {
  const { permissions, isObjectless } = useAutomationsContext();

  const [isAllowedAddButton] = useCrmOrgPermissions([permissions.create]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState('');

  const open = Boolean(anchorEl);
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (automationVariant === AutomationFormVariant.MARKETING_ATTRIBUTIONS) {
        return onSelect('Opportunity');
      }

      if (isObjectless) {
        return onSelect('');
      }
      if (!anchorEl) {
        setAnchorEl(event.currentTarget);
        setSearch('');
      }
    },
    [automationVariant, isObjectless, onSelect, anchorEl, setAnchorEl, setSearch],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const { objectTypes } = useObjectTypesWithFetch({
    crmOrgId,
  });

  const filteredObjectTypes = useMemo(
    () =>
      (
        objectTypesPropertyFilter({
          objectTypes,
          filters: {
            triggerable: true,
            updateable: true,
            includeBig5: true,
          },
        }) ?? []
      ).map((objectType) => ({
        label: objectType.label,
        value: objectType.objectType,
      })),
    [objectTypes],
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value),
    [],
  );
  const handleClearSearch = useCallback(() => setSearch(''), []);

  const filteredItems = useMemo(
    () => filterItemsBySearch(filteredObjectTypes, search, (item) => item.label),
    [filteredObjectTypes, search],
  );

  return (
    <Box
      sx={{
        position: 'relative',
      }}
      id="add-automation-button-wrapper"
    >
      <RestrictedTooltip
        to={[permissions.create]}
        notAllowedTitle={`To create a new ${getTypeNameFromVariant(
          automationVariant,
        )}, please contact your admin.`}
      >
        <Button
          variant={variant}
          disabled={!isAllowedAddButton || disabled}
          startIconName={startIconName}
          size="small"
          onClick={handleClick}
        >
          {buttonText}
        </Button>
      </RestrictedTooltip>

      <Popover
        elevation={0}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        {...popoverProps}
      >
        <Box padding={1}>
          <SearchInput
            withFixedMagnifyingGlassIcon
            TextFieldProps={{
              value: search,
              onChange: handleSearchChange,
            }}
            onClearButtonClick={handleClearSearch}
          />
          <Box sx={{ maxHeight: ITEM_HEIGHT * 10, overflow: 'auto', marginTop: 1 }}>
            {filteredItems.map((item) => (
              <MenuItem
                value={item.value}
                key={item.value}
                onClick={() => {
                  onSelect(item.value);
                  handleClose();
                }}
                disableRipple
              >
                <ObjectTypeLabelAndKeyTag
                  objectType={item.value}
                  label={highlightMatch(item.label, search)}
                />
              </MenuItem>
            ))}
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};
